import { Link, getRouteApi, useNavigate, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { BOARD_LIMITS, type BoardDetails, type BoardObject, type BoardObjectDetails, type NewBoardObject, type ShapeKind } from '@realmery/realm-core';
import { fitView, usePanZoom } from '../canvas/usePanZoom.ts';
import { prepareBoardImage } from '../canvas/boardImage.ts';
import { ColorField } from '../canvas/ColorField.tsx';
import { RealmClientError, realms } from '../db/client.ts';
import { realmFile, type BoardView } from '../db/protocol.ts';
import { ImageFileError } from '../editor/imageFiles.ts';
import { settings } from '../settings.ts';

/**
 * The Idea Board (docs/PARITY-STEP-4-PLAN.md section 3), as the desktop's `BoardPane`: the
 * realm's boards as cards, and a board open on the shared canvas with its notes, cards,
 * pictures, shapes and the arrows between them. Drag an object to move it, the background
 * to pan; the wheel or a pinch zooms. Clicking (or tapping) an object picks it, for
 * **Edit...**, **Bring to Front** and **Delete**, which the right-click and the keys
 * (Enter, Delete, the arrows) reach too. A card's entry opens it; its map shows the map's
 * name until the Atlas comes in slice 2, and its event opens the event's timeline.
 */

const galleryRoute = getRouteApi('/_app/realm/$realmKey/boards');
const boardRoute = getRouteApi('/_app/realm/$realmKey/boards/$boardId');
const realmRoute = getRouteApi('/_app/realm/$realmKey');

const MIN_SCALE = 0.25;
const MAX_SCALE = 3;
/** A press that moves less than this, in screen pixels, is a click (`CLICK_DRAG_THRESHOLD`). */
const CLICK_DRAG = 5;

const message = (e: unknown, fallback: string) => (e instanceof RealmClientError || e instanceof ImageFileError ? e.message : fallback);
const plural = (n: number, one: string, many: string) => `${n.toLocaleString('en')} ${n === 1 ? one : many}`;
/** What an object is called in a message (`BoardPane.displayTitle`). */
const titleOf = (o: BoardObject) => (o.text.trim() === '' ? { TEXT: 'Note', CARD: 'Card', IMAGE: 'Picture', SHAPE: o.shapeKind === 'ELLIPSE' ? 'Ellipse' : 'Box', ARROW: 'Arrow' }[o.kind] : o.text);

// ---- the gallery ----

export function BoardsPage() {
  const boards = galleryRoute.useLoaderData();
  const { realmKey } = galleryRoute.useParams();
  const file = realmFile(realmKey);
  const router = useRouter();
  const navigate = useNavigate();
  const [making, setMaking] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState('');

  async function run(work: () => Promise<unknown>, fallback: string) {
    setError('');
    try {
      await work();
      setDeleting(null);
      await router.invalidate();
    } catch (e) {
      setError(message(e, fallback));
    }
  }

  return (
    <article className="document boards-page">
      <p className="eyebrow">Idea Board</p>
      <h1>Idea Boards</h1>
      <p className="lead">A corkboard for brainstorming: notes, cards, shapes and pictures placed anywhere and joined with arrows, with cards that link to the realm's entries, maps and events.</p>
      <div className="actions">
        <button type="button" className="primary" onClick={() => setMaking(true)}>
          New board
        </button>
      </div>
      {making && (
        <BoardForm
          heading="New board"
          submit="Make board"
          onCancel={() => setMaking(false)}
          onSave={(details) =>
            run(async () => {
              const { id } = await realms.createBoard(file, details);
              await navigate({ to: '/realm/$realmKey/boards/$boardId', params: { realmKey, boardId: id } });
            }, 'The board could not be made.')
          }
        />
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {boards.length === 0 ? (
        <p className="meta">No boards yet. Make one to start brainstorming.</p>
      ) : (
        <ul className="table-gallery board-gallery">
          {boards.map((b) => (
            <li key={b.id}>
              <Link to="/realm/$realmKey/boards/$boardId" params={{ realmKey, boardId: b.id }}>
                📌 {b.name}
              </Link>
              <span className="meta">{plural(b.objectCount, 'object', 'objects')}</span>
              <button
                type="button"
                className="quiet"
                onClick={() => (settings()['general.confirmDeletes'] ? setDeleting(b.id) : void run(() => realms.deleteBoard(file, b.id), 'The board could not be deleted.'))}
              >
                Delete…
              </button>
              {b.description && <p className="pre-line board-description">{b.description}</p>}
              {deleting === b.id && (
                <Confirm
                  label={`Delete ${b.name}`}
                  text={`Delete “${b.name}”? ${b.objectCount === 0 ? 'It has nothing on it.' : `Its ${plural(b.objectCount, 'object goes', 'objects go')} with it.`}`}
                  button="Delete board"
                  onCancel={() => setDeleting(null)}
                  onConfirm={() => run(() => realms.deleteBoard(file, b.id), 'The board could not be deleted.')}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

// ---- a board ----

type Editing = { readonly kind: 'board' } | { readonly kind: 'object'; readonly id: string } | null;

export function BoardPage() {
  const view = boardRoute.useLoaderData() as BoardView | null;
  const { realmKey } = boardRoute.useParams();
  if (!view) {
    return (
      <article className="document">
        <h1>Board not found</h1>
        <p>
          This board isn't in the realm any more. <Link to="/realm/$realmKey/boards" params={{ realmKey }}>All boards</Link>
        </p>
      </article>
    );
  }
  return <OpenBoard key={view.board.id} view={view} realmKey={realmKey} />;
}

function OpenBoard({ view, realmKey }: { view: BoardView; realmKey: string }) {
  const { entries } = realmRoute.useLoaderData();
  const file = realmFile(realmKey);
  const router = useRouter();
  const { board, objects } = view;
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [connecting, setConnecting] = useState<{ from: string | null } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [shapeMenu, setShapeMenu] = useState(false);
  /** Where objects being dragged (or moved by key) are, before the move is saved. */
  const [moved, setMoved] = useState<ReadonlyMap<string, { x: number; y: number }>>(new Map());
  const pictureInput = useRef<HTMLInputElement>(null);
  const pz = usePanZoom(MIN_SCALE, MAX_SCALE);
  const { box, svg, size, view: v, setView, dragged, handlers, onKeyDown } = pz;

  const byId = useMemo(() => new Map(objects.map((o) => [o.id, o])), [objects]);
  const at = (o: BoardObject) => moved.get(o.id) ?? { x: o.x, y: o.y };
  const pictures = usePictureUrls(objects);

  async function run(work: () => Promise<unknown>, fallback: string): Promise<boolean> {
    setError('');
    try {
      await work();
      await router.invalidate();
      return true;
    } catch (e) {
      setError(message(e, fallback));
      await router.invalidate();
      return false;
    }
  }

  const fit = useCallback(() => {
    const placed = objects.filter((o) => o.kind !== 'ARROW');
    if (placed.length === 0) return setView({ scale: 1, x: 24, y: 24 });
    const bounds = {
      minX: Math.min(...placed.map((o) => o.x)),
      minY: Math.min(...placed.map((o) => o.y)),
      maxX: Math.max(...placed.map((o) => o.x + o.width)),
      maxY: Math.max(...placed.map((o) => o.y + o.height)),
    };
    setView(fitView(bounds, size, 24, MIN_SCALE, Math.min(MAX_SCALE, 1)));
    // Only when the board first opens, or the window changes size: not after every edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, setView]);
  useEffect(() => fit(), [fit]);

  // A deleted object can't stay picked.
  useEffect(() => {
    if (selected && !byId.has(selected)) setSelected(null);
  }, [selected, byId]);

  const add = (object: NewBoardObject) =>
    run(async () => {
      const { id } = await realms.createBoardObject(file, board.id, object);
      setSelected(id);
    }, 'That could not be added to the board.');

  async function addPicture(chosen: File | undefined) {
    if (!chosen) return;
    setError('');
    try {
      const data = await prepareBoardImage(chosen);
      await add({ kind: 'IMAGE', data });
    } catch (e) {
      setError(message(e, 'That picture could not be added.'));
    }
  }

  const remove = (id: string) =>
    run(async () => {
      await realms.deleteBoardObject(file, id);
      setConfirming(null);
      setSelected(null);
      setEditing(null);
    }, 'That could not be deleted.');
  const askRemove = (id: string) => (settings()['general.confirmDeletes'] ? setConfirming(id) : void remove(id));

  /** A click on an object: the end of an arrow while connecting, otherwise picking it. */
  function clicked(o: BoardObject) {
    if (!connecting) return setSelected(o.id);
    if (o.kind === 'ARROW') return;
    if (connecting.from === null) return setConnecting({ from: o.id });
    if (connecting.from === o.id) return;
    const from = connecting.from;
    setConnecting(null);
    void run(async () => {
      const { id } = await realms.createArrow(file, board.id, from, o.id);
      setSelected(id);
    }, 'Those could not be joined.');
  }

  // ---- dragging an object ----
  const press = useRef<{ id: string; pointer: number; startX: number; startY: number; from: { x: number; y: number } } | null>(null);

  function onObjectDown(event: PointerEvent<SVGGElement>, o: BoardObject) {
    if (event.button !== 0) return;
    event.stopPropagation();
    (event.currentTarget as Element).setPointerCapture(event.pointerId);
    press.current = { id: o.id, pointer: event.pointerId, startX: event.clientX, startY: event.clientY, from: at(o) };
  }

  function onObjectMove(event: PointerEvent<SVGGElement>) {
    const p = press.current;
    if (!p || p.pointer !== event.pointerId) return;
    event.stopPropagation();
    if (connecting || byId.get(p.id)?.kind === 'ARROW') return;
    const dx = event.clientX - p.startX;
    const dy = event.clientY - p.startY;
    if (Math.hypot(dx, dy) <= CLICK_DRAG && !moved.has(p.id)) return;
    setMoved((m) => new Map(m).set(p.id, { x: p.from.x + dx / v.scale, y: p.from.y + dy / v.scale }));
  }

  function onObjectUp(event: PointerEvent<SVGGElement>, o: BoardObject) {
    const p = press.current;
    if (!p || p.pointer !== event.pointerId) return;
    event.stopPropagation();
    press.current = null;
    const now = moved.get(o.id);
    if (now && Math.hypot(event.clientX - p.startX, event.clientY - p.startY) > CLICK_DRAG) {
      setSelected(o.id);
      void save(o.id, now);
    } else {
      clicked(o);
    }
  }

  async function save(id: string, to: { x: number; y: number }) {
    await run(() => realms.moveBoardObject(file, id, Math.round(to.x * 10) / 10, Math.round(to.y * 10) / 10), 'That could not be moved.');
    setMoved((m) => {
      const next = new Map(m);
      next.delete(id);
      return next;
    });
  }

  /** The keys on a picked object: Enter edits it, Delete deletes it, the arrows move it (Shift for bigger steps). */
  function onObjectKey(event: KeyboardEvent<SVGGElement>, o: BoardObject) {
    const step = event.shiftKey ? 50 : 10;
    const by: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const move = by[event.key];
    if (move && o.kind !== 'ARROW') {
      event.preventDefault();
      event.stopPropagation();
      const from = at(o);
      void save(o.id, { x: from.x + move[0], y: from.y + move[1] });
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      clicked(o);
      if (!connecting && o.kind !== 'ARROW' && o.kind !== 'IMAGE' && event.key === 'Enter') setEditing({ kind: 'object', id: o.id });
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      event.stopPropagation();
      setSelected(o.id);
      askRemove(o.id);
    }
  }

  function onBoardKey(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (connecting) setConnecting(null);
      else setSelected(null);
      return;
    }
    onKeyDown(event, fit);
  }

  const pick = selected ? byId.get(selected) : undefined;
  const status = connecting
    ? connecting.from === null
      ? 'Click an object, then click another to connect them.'
      : `Connecting from “${titleOf(byId.get(connecting.from)!)}” - click the target object.`
    : '';

  const placed = objects.filter((o) => o.kind !== 'ARROW');
  const arrows = objects.filter((o) => o.kind === 'ARROW');
  const centre = (o: BoardObject) => {
    const p = at(o);
    return { x: p.x + o.width / 2, y: p.y + o.height / 2 };
  };
  const objectProps = (o: BoardObject) => ({
    tabIndex: 0,
    role: 'button',
    'aria-label': describe(o, byId),
    'aria-pressed': selected === o.id,
    className: ['board-object', `board-${o.kind.toLowerCase()}`, selected === o.id ? 'selected' : '', connecting?.from === o.id ? 'connecting' : ''].filter(Boolean).join(' '),
    onPointerDown: (e: PointerEvent<SVGGElement>) => onObjectDown(e, o),
    onPointerMove: onObjectMove,
    onPointerUp: (e: PointerEvent<SVGGElement>) => onObjectUp(e, o),
    onPointerCancel: () => (press.current = null),
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    // Tabbing to an object picks it, for the keys and the Picked buttons.
    onFocus: () => {
      if (!connecting) setSelected(o.id);
    },
    onKeyDown: (e: KeyboardEvent<SVGGElement>) => onObjectKey(e, o),
    onContextMenu: (e: React.MouseEvent) => {
      e.preventDefault();
      setSelected(o.id);
    },
  });

  return (
    <article className="document board-page">
      <p className="eyebrow">
        <Link to="/realm/$realmKey/boards" params={{ realmKey }}>
          ← All boards
        </Link>
      </p>
      <h1>{board.name}</h1>
      {board.description && <p className="lead pre-line">{board.description}</p>}
      <div className="actions board-toolbar" role="toolbar" aria-label="Board">
        <button type="button" title="Rename this board or change its description" onClick={() => setEditing({ kind: 'board' })}>
          Edit board
        </button>
        <button type="button" title="Add a sticky note of text" onClick={() => void add({ kind: 'TEXT' })}>
          + Text
        </button>
        <button type="button" title="Add a card - a title and notes, linked to an entry, map, or event" onClick={() => void add({ kind: 'CARD' })}>
          + Card
        </button>
        <span className="board-shape-menu">
          <button type="button" title="Add a box or an ellipse - to group things together" aria-expanded={shapeMenu} onClick={() => setShapeMenu(!shapeMenu)}>
            + Shape
          </button>
          {shapeMenu && (
            <span role="group" aria-label="Shape">
              {(['RECTANGLE', 'ELLIPSE'] as const satisfies readonly ShapeKind[]).map((shapeKind) => (
                <button
                  key={shapeKind}
                  type="button"
                  onClick={() => {
                    setShapeMenu(false);
                    void add({ kind: 'SHAPE', shapeKind });
                  }}
                >
                  {shapeKind === 'RECTANGLE' ? '▭ Box' : '◯ Ellipse'}
                </button>
              ))}
            </span>
          )}
        </span>
        <button type="button" title="Add a picture from a file" onClick={() => pictureInput.current?.click()}>
          + Image
        </button>
        <input
          ref={pictureInput}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/bmp"
          hidden
          aria-label="Picture to add"
          onChange={(e) => {
            void addPicture(e.currentTarget.files?.[0]);
            e.currentTarget.value = '';
          }}
        />
        <button
          type="button"
          aria-pressed={connecting !== null}
          title="Draw an arrow: click one object, then another - click again to cancel"
          onClick={() => setConnecting(connecting ? null : { from: null })}
        >
          🔗 Connect
        </button>
        <span className="meta" role="status">
          {status}
        </span>
      </div>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {editing?.kind === 'board' && (
        <BoardForm
          heading="Edit board"
          submit="Save"
          initial={board}
          onCancel={() => setEditing(null)}
          onSave={(details) => run(() => realms.updateBoard(file, board.id, details), 'The board could not be saved.').then((ok) => ok && setEditing(null))}
        />
      )}
      {pick && (
        <div className="board-selection" role="group" aria-label="Picked">
          <span>
            Picked: <strong>{titleOf(pick)}</strong>
          </span>
          {pick.kind !== 'ARROW' && pick.kind !== 'IMAGE' && (
            <button type="button" onClick={() => setEditing({ kind: 'object', id: pick.id })}>
              Edit…
            </button>
          )}
          {pick.kind !== 'ARROW' && (
            <button type="button" onClick={() => void run(() => realms.bringToFront(file, pick.id), 'That could not be brought to the front.')}>
              Bring to Front
            </button>
          )}
          <button type="button" className="quiet" onClick={() => askRemove(pick.id)}>
            {pick.kind === 'ARROW' ? 'Delete Arrow' : 'Delete…'}
          </button>
        </div>
      )}
      {confirming && byId.get(confirming) && (
        <Confirm
          label={`Delete ${titleOf(byId.get(confirming)!)}`}
          text={deletionText(byId.get(confirming)!, arrows)}
          button="Delete"
          onCancel={() => setConfirming(null)}
          onConfirm={() => remove(confirming)}
        />
      )}
      {editing?.kind === 'object' && byId.get(editing.id) && (
        <ObjectForm
          key={editing.id}
          object={byId.get(editing.id)!}
          view={view}
          entries={entries}
          onCancel={() => setEditing(null)}
          onSave={(details) => run(() => realms.updateBoardObject(file, editing.id, details), 'That could not be saved.').then((ok) => ok && setEditing(null))}
        />
      )}
      <div className="graph-canvas board-canvas" ref={box}>
        <div className="graph-zoom" role="group" aria-label="Zoom">
          <button type="button" onClick={() => pz.zoomAt(size.width / 2, size.height / 2, 1.25)} aria-label="Zoom in">
            +
          </button>
          <button type="button" onClick={() => pz.zoomAt(size.width / 2, size.height / 2, 0.8)} aria-label="Zoom out">
            −
          </button>
          <button type="button" onClick={fit}>
            Fit
          </button>
        </div>
        <svg
          ref={svg}
          role="group"
          aria-label={`${board.name}: ${plural(placed.length, 'object', 'objects')} and ${plural(arrows.length, 'arrow', 'arrows')}. Drag or use the arrow keys to move around, + and − to zoom, 0 to fit. Tab to an object to pick it.`}
          tabIndex={0}
          {...handlers}
          onClick={() => {
            if (!dragged.current && !connecting) setSelected(null);
          }}
          onKeyDown={onBoardKey}
        >
          <g transform={`translate(${v.x} ${v.y}) scale(${v.scale})`}>
            {/* Arrows under everything, joining the middles of what they join, as the desktop draws them. */}
            {arrows.map((a) => {
              const from = a.fromObjectId ? byId.get(a.fromObjectId) : undefined;
              const to = a.toObjectId ? byId.get(a.toObjectId) : undefined;
              if (!from || !to) return null;
              const [p, q] = [centre(from), centre(to)];
              return (
                <g key={a.id} {...objectProps(a)}>
                  <line x1={p.x} y1={p.y} x2={q.x} y2={q.y} className="board-arrow-hit" />
                  <line x1={p.x} y1={p.y} x2={q.x} y2={q.y} stroke={a.colorHex} strokeWidth={2} />
                </g>
              );
            })}
            {placed.map((o) => {
              const p = at(o);
              return (
                <g key={o.id} {...objectProps(o)} transform={`translate(${p.x} ${p.y})`}>
                  <ObjectArt object={o} picture={pictures.get(o.id)} view={view} entries={entries} realmKey={realmKey} />
                </g>
              );
            })}
          </g>
        </svg>
        {placed.length === 0 && <p className="board-empty meta">This board is empty. Add a note, a card, a shape or a picture to begin.</p>}
      </div>
    </article>
  );
}

/** What a screen reader hears for an object. */
function describe(o: BoardObject, byId: ReadonlyMap<string, BoardObject>): string {
  if (o.kind === 'ARROW') {
    const from = o.fromObjectId ? byId.get(o.fromObjectId) : undefined;
    const to = o.toObjectId ? byId.get(o.toObjectId) : undefined;
    return `Arrow from ${from ? titleOf(from) : '?'} to ${to ? titleOf(to) : '?'}`;
  }
  const kind = { TEXT: 'Note', CARD: 'Card', IMAGE: 'Picture', SHAPE: 'Shape' }[o.kind];
  return o.text.trim() === '' ? kind : `${kind}: ${o.text}`;
}

function deletionText(o: BoardObject, arrows: readonly BoardObject[]): string {
  if (o.kind === 'ARROW') return 'Delete this arrow?';
  const joined = arrows.filter((a) => a.fromObjectId === o.id || a.toObjectId === o.id).length;
  return `Delete this ${o.kind === 'CARD' ? 'card' : o.kind === 'TEXT' ? 'note' : o.kind === 'IMAGE' ? 'picture' : 'shape'}?${joined === 0 ? '' : ` ${plural(joined, 'arrow goes', 'arrows go')} with it.`}`;
}

/** Each picture as a URL the board can show, made once per picture and let go when it's gone. */
function usePictureUrls(objects: readonly BoardObject[]): ReadonlyMap<string, string> {
  const made = useRef(new Map<string, { data: Uint8Array; url: string }>());
  const urls = useMemo(() => {
    const now = new Map<string, string>();
    for (const o of objects) {
      if (o.kind !== 'IMAGE' || !o.imageData) continue;
      let known = made.current.get(o.id);
      if (!known || known.data.byteLength !== o.imageData.byteLength) {
        if (known) URL.revokeObjectURL(known.url);
        known = { data: o.imageData, url: URL.createObjectURL(new Blob([o.imageData as Uint8Array<ArrayBuffer>], { type: o.imageMimeType || 'image/png' })) };
        made.current.set(o.id, known);
      }
      now.set(o.id, known.url);
    }
    for (const [id, known] of made.current) {
      if (!now.has(id)) {
        URL.revokeObjectURL(known.url);
        made.current.delete(id);
      }
    }
    return now;
  }, [objects]);
  useEffect(
    () => () => {
      for (const known of made.current.values()) URL.revokeObjectURL(known.url);
      made.current.clear();
    },
    [],
  );
  return urls;
}

function ObjectArt({ object: o, picture, view, entries, realmKey }: { object: BoardObject; picture: string | undefined; view: BoardView; entries: readonly { id: string; title: string }[]; realmKey: string }) {
  const { width, height } = o;
  switch (o.kind) {
    case 'SHAPE':
      return o.shapeKind === 'ELLIPSE' ? (
        <ellipse cx={width / 2} cy={height / 2} rx={width / 2} ry={height / 2} fill={o.colorHex} className="board-shape-art" />
      ) : (
        <rect width={width} height={height} fill={o.colorHex} className="board-shape-art" />
      );
    case 'IMAGE':
      return picture ? (
        <image href={picture} width={width} height={height} preserveAspectRatio="none" />
      ) : (
        <foreignObject width={width} height={height}>
          <div className="board-box">(image error)</div>
        </foreignObject>
      );
    case 'TEXT':
      return (
        <foreignObject width={width} height={height}>
          <div className="board-box board-note-box" style={{ background: o.colorHex }}>
            {o.text}
          </div>
        </foreignObject>
      );
    case 'CARD': {
      const entry = o.linkedEntryId ? entries.find((e) => e.id === o.linkedEntryId) : undefined;
      const map = o.linkedMapId ? view.maps.find((m) => m.id === o.linkedMapId) : undefined;
      const event = o.linkedEventId ? view.events.find((e) => e.event.id === o.linkedEventId) : undefined;
      return (
        <foreignObject width={width} height={height}>
          <div className="board-box board-card-box" style={{ background: o.colorHex }}>
            <strong>{o.text}</strong>
            {o.notes.trim() !== '' && <span className="pre-line board-card-notes">{o.notes}</span>}
            {o.linkedEntryId && (
              <Link
                to="/realm/$realmKey/entry/$entryId"
                params={{ realmKey, entryId: o.linkedEntryId }}
                search={{}}
                className="chip"
                onPointerDown={(e) => e.stopPropagation()}
                onPointerUp={(e) => e.stopPropagation()}
                tabIndex={-1}
              >
                {entry?.title ?? '(missing entry)'} →
              </Link>
            )}
            {o.linkedMapId &&
              (map ? (
                <Link
                  to="/realm/$realmKey/atlas/$mapId"
                  params={{ realmKey, mapId: map.id }}
                  search={{}}
                  className="meta"
                  onPointerDown={(e) => e.stopPropagation()}
                  onPointerUp={(e) => e.stopPropagation()}
                  tabIndex={-1}
                >
                  🗺 {map.name}
                </Link>
              ) : (
                <span className="meta">🗺 linked map</span>
              ))}
            {o.linkedEventId &&
              (event ? (
                <Link
                  to="/realm/$realmKey/chronology"
                  params={{ realmKey }}
                  search={{ timeline: event.timeline.id }}
                  className="meta"
                  onPointerDown={(e) => e.stopPropagation()}
                  onPointerUp={(e) => e.stopPropagation()}
                  tabIndex={-1}
                >
                  🕒 {event.event.title}
                </Link>
              ) : (
                <span className="meta">🕒 linked event</span>
              ))}
          </div>
        </foreignObject>
      );
    }
    default:
      return null;
  }
}

// ---- forms ----

function BoardForm({ heading, submit, initial, onSave, onCancel }: { heading: string; submit: string; initial?: BoardDetails; onSave: (details: BoardDetails) => Promise<unknown>; onCancel: () => void }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  return (
    <form
      className="panel"
      aria-label={heading}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void onSave({ name, description });
      }}
    >
      <h3>{heading}</h3>
      <label>
        Name
        <input value={name} required maxLength={BOARD_LIMITS.name} autoFocus placeholder="e.g. Villain Motivation" onChange={(e) => setName(e.currentTarget.value)} />
      </label>
      <label>
        Description
        <textarea value={description} rows={3} maxLength={BOARD_LIMITS.text} placeholder="Optional description" onChange={(e) => setDescription(e.currentTarget.value)} />
      </label>
      <FormButtons submit={submit} onCancel={onCancel} />
    </form>
  );
}

/** Edit...: a note's text and colour, a card's title, notes, colour and links, a shape's colour (`BoardDialogs`). */
function ObjectForm({
  object: o,
  view,
  entries,
  onSave,
  onCancel,
}: {
  object: BoardObject;
  view: BoardView;
  entries: readonly { id: string; title: string; typeName: string }[];
  onSave: (details: BoardObjectDetails) => Promise<unknown>;
  onCancel: () => void;
}) {
  const [text, setText] = useState(o.text);
  const [notes, setNotes] = useState(o.notes);
  const [colorHex, setColorHex] = useState(/^#[0-9A-Fa-f]{6}$/.test(o.colorHex) ? o.colorHex : '#E9E1F5');
  const [entryId, setEntryId] = useState(o.linkedEntryId ?? '');
  const [mapId, setMapId] = useState(o.linkedMapId ?? '');
  const [eventId, setEventId] = useState(o.linkedEventId ?? '');
  const heading = { TEXT: 'Edit note', CARD: 'Edit card', SHAPE: 'Edit shape' }[o.kind as 'TEXT' | 'CARD' | 'SHAPE'];
  const card = o.kind === 'CARD';
  return (
    <form
      className="panel board-object-form"
      aria-label={heading}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        void onSave({ text, notes, colorHex, linkedEntryId: card && entryId ? entryId : null, linkedMapId: card && mapId ? mapId : null, linkedEventId: card && eventId ? eventId : null });
      }}
    >
      <h3>{heading}</h3>
      {o.kind === 'TEXT' && (
        <label>
          Text
          <textarea value={text} rows={4} maxLength={BOARD_LIMITS.text} autoFocus placeholder="Note text" onChange={(e) => setText(e.currentTarget.value)} />
        </label>
      )}
      {card && (
        <>
          <label>
            Title
            <input value={text} required maxLength={BOARD_LIMITS.text} autoFocus placeholder="e.g. The Villain" onChange={(e) => setText(e.currentTarget.value)} />
          </label>
          <label>
            Notes
            <textarea value={notes} rows={3} maxLength={BOARD_LIMITS.text} placeholder="Optional notes" onChange={(e) => setNotes(e.currentTarget.value)} />
          </label>
        </>
      )}
      <ColorField label="Colour" value={colorHex} onChange={setColorHex} />
      {card && (
        <>
          <label>
            Linked entry
            <select value={entryId} onChange={(e) => setEntryId(e.currentTarget.value)}>
              <option value="">(none)</option>
              {entries.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title} ({e.typeName})
                </option>
              ))}
            </select>
          </label>
          <label>
            Linked map
            <select value={mapId} onChange={(e) => setMapId(e.currentTarget.value)}>
              <option value="">(none)</option>
              {view.maps.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Linked event
            <select value={eventId} onChange={(e) => setEventId(e.currentTarget.value)}>
              <option value="">(none)</option>
              {view.events.map(({ event, timeline }) => (
                <option key={event.id} value={event.id}>
                  {event.title} ({timeline.name})
                </option>
              ))}
            </select>
          </label>
        </>
      )}
      <FormButtons submit="Save" onCancel={onCancel} />
    </form>
  );
}

function FormButtons({ submit, onCancel }: { submit: string; onCancel: () => void }) {
  return (
    <div className="actions">
      <button type="submit" className="primary">
        {submit}
      </button>
      <button type="button" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

function Confirm({ label, text, button, onConfirm, onCancel }: { label: string; text: ReactNode; button: string; onConfirm: () => Promise<unknown>; onCancel: () => void }) {
  return (
    <div className="panel confirm" role="alertdialog" aria-label={label}>
      <p>{text}</p>
      <div className="actions">
        <button type="button" className="danger" onClick={() => void onConfirm()}>
          {button}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
